import React from "react";
import { formatDual } from "@/lib/currency";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Show two currencies side-by-side. Primary = user's language currency,
 * secondary = the other of {INR, EUR}. Both derived from the same EUR base amount.
 *
 * Props:
 *   amount   -- EUR amount (number)
 *   size     -- "sm" | "md" | "lg" | "xl" — controls typography
 *   inline   -- if true, secondary sits on the same line ("₹8,010 · €89")
 *              if false (default), stacks with the secondary muted underneath
 *   className -- additional wrapper classes
 */
export default function DualPrice({ amount, size = "md", inline = false, className = "" }) {
    const { lang } = useI18n();
    const { primary, secondary } = formatDual(amount, lang);

    const sizeMap = {
        sm: { p: "text-xs sm:text-sm", s: "text-[10px] sm:text-[11px]" },
        md: { p: "text-sm sm:text-base", s: "text-[10px] sm:text-xs" },
        lg: { p: "font-serif text-2xl", s: "text-xs sm:text-sm" },
        xl: { p: "font-serif text-3xl sm:text-4xl", s: "text-[11px] sm:text-sm" },
    };
    const S = sizeMap[size] || sizeMap.md;

    if (inline) {
        return (
            <span data-testid="dual-price" className={`inline-flex items-baseline gap-2 ${className}`}>
                <span className={`text-brass-400 ${S.p} tabular-nums`}>{primary}</span>
                <span className={`text-bone-300/60 ${S.s} tabular-nums`}>· {secondary}</span>
            </span>
        );
    }
    return (
        <span data-testid="dual-price" className={`inline-flex flex-col leading-none ${className}`}>
            <span className={`text-brass-400 ${S.p} tabular-nums leading-none`}>{primary}</span>
            <span className={`text-bone-300/55 ${S.s} tabular-nums mt-1 tracking-wide`}>{secondary}</span>
        </span>
    );
}
